import { composeClassNames } from "@faedev/editor-utils";
import { useRouter } from "next/router";
import { CSSProperties, ReactElement } from "react";
import { Badge, Card, OverlayTrigger, Tooltip } from "react-bootstrap";
import ReactMarkdown from "react-markdown";
import Heading from "src/components/Heading";
import Translate from "src/components/Translate";
import constants from "src/constants";
import gutter from "src/functions/gutter";
import translate from "src/functions/translate";
import CheckNoBorder from "src/icons/CheckNoBorder";
import Info from "src/icons/Info";
import { sBadge } from "src/styles/sBadge.module.scss";
import { sSubscriptionCard } from "src/styles/sSubscriptionCard.module.scss";
import Paragraph from "./Paragraph";

type Props = {
  activeId?: number;
  className?: (string | string) | null | undefined;
  cta?: ReactElement;
  description: string;
  disableHover?: boolean;
  id: number;
  setActive?: (setActive: number) => void;
  style?: CSSProperties | null | undefined;
  title: string;
  pricePage?: boolean;
  recommended?: number;
  validTill?: string;
  price?: ReactElement;
};

function SubscriptionCard({
  activeId = 0,
  className,
  cta,
  description,
  disableHover,
  id,
  price,
  setActive,
  style,
  title,
  pricePage = false,
  recommended = 0,
  validTill,
}: Props) {
  const { locale: lang } = useRouter();
  const isActive = activeId === id;
  const isRecommended = recommended === id;
  const date = new Date(validTill);

  const validTillDate =
    date.getDate() + ". " + (date.getMonth() + 1) + ". " + date.getFullYear();

  return price && title ? (
    <Card
      className={composeClassNames([
        sSubscriptionCard,
        className,
        isActive ? "active" : "",
        disableHover ? "disable-hover" : "",
      ])}
      onClick={() => setActive(id)}
      style={style}
    >
      <Card.Body>
        {isRecommended && (
          <div className="recommended">
            <Translate tKey="recommended" />
          </div>
        )}
        <Card.Title>
          <Heading type="h2" title={translate(title, lang)} />
        </Card.Title>
        <div className="card-text">
          <ReactMarkdown>{description}</ReactMarkdown>
        </div>
        {!validTill && (
          <div className="prices">
            {!pricePage && (
              <div className="check-icon">
                {activeId === id && <CheckNoBorder />}
              </div>
            )}
            {price}
          </div>
        )}
        {validTill && (
          <>
            <Paragraph
              size="sm"
              width={`regular`}
              align="center"
              style={gutter(`margin`, `top`, `0`)}
            >
              <Translate
                tKey={`Congratulations, your subscription is active. Now you can enjoy all the videos anytime, anywhere and unlimited.`}
              />
            </Paragraph>
            <Badge
              pill={true}
              variant={`light`}
              className={composeClassNames([sBadge, "valid-till"])}
              style={gutter(`padding`, `all`, `nm`)}
            >
              <Translate tKey={`Valid till`} />: {validTillDate}
              <OverlayTrigger
                placement="top"
                delay={constants.Intervals.overlayDelay}
                overlay={
                  <Tooltip id="table-item-info">
                    <Paragraph
                      size="sm"
                      width={`regular`}
                      align="center"
                      style={gutter(`margin`, `horizontal`, `sm`)}
                    >
                      <Translate
                        tKey={`The subscription will automatically renew after the specified date. Automatic renewal occurs during the last day of the subscription.`}
                      />
                    </Paragraph>
                  </Tooltip>
                }
              >
                <Info style={gutter(`margin`, `left`, `tn`)} />
              </OverlayTrigger>
            </Badge>
          </>
        )}
        {cta && cta}
      </Card.Body>
    </Card>
  ) : null;
}

export default SubscriptionCard;
